/**
 * Session API Service
 *
 * Handles session management, snapshots, auto-save, and recovery
 */

import { apiClient } from './client';

class SessionAPI {
  async createSession(data: {
    projectId: string;
    currentStep: string;
    sessionData: Record<string, any>;
  }): Promise<{ session_token: string; message: string }> {
    const response = await apiClient.post('/api/v1/sessions/create', {
      analysis_run_id: data.projectId,
      current_step: data.currentStep,
      session_data: data.sessionData
    });
    return response.data;
  }

  async getActiveSession(projectId: string): Promise<any | null> {
    try {
      const response = await apiClient.get(`/api/v1/sessions/active/${projectId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async updateSession(sessionToken: string, data: {
    currentStep?: string;
    sessionData?: Record<string, any>;
  }): Promise<{ message: string }> {
    const response = await apiClient.put(`/api/v1/sessions/${sessionToken}`, {
      current_step: data.currentStep,
      session_data: data.sessionData
    });
    return response.data;
  }

  async deactivateSession(sessionToken: string): Promise<{ message: string }> {
    const response = await apiClient.delete(`/api/v1/sessions/${sessionToken}`);
    return response.data;
  }

  async saveSnapshot(projectId: string, data: {
    snapshotType: string;
    projectState: Record<string, any>;
    workflowStep: string;
    description?: string;
  }): Promise<{ snapshot_id: string; message: string }> {
    const response = await apiClient.post(`/api/v1/sessions/${projectId}/snapshots`, {
      snapshot_type: data.snapshotType,
      workflow_state: data.projectState,
      workflow_step: data.workflowStep,
      description: data.description
    });
    return response.data;
  }

  async autoSave(data: {
    projectId: string;
    currentState: Record<string, any>;
    workflowStep: string;
  }): Promise<{ message: string; timestamp: string }> {
    const response = await apiClient.post('/api/v1/sessions/auto-save', {
      analysis_run_id: data.projectId,
      current_state: data.currentState,
      workflow_step: data.workflowStep
    });
    return response.data;
  }

  async getRecoverySessions(): Promise<any[]> {
    const response = await apiClient.get('/api/v1/sessions/recovery');
    return Array.isArray(response.data) ? response.data : [];
  }

  async recoverSession(sessionToken: string): Promise<any> {
    const response = await apiClient.post(`/api/v1/sessions/recovery/${sessionToken}`);
    return response.data;
  }

  async getLatestSnapshot(projectId: string, snapshotType?: string): Promise<any | null> {
    try {
      const url = `/api/v1/sessions/${projectId}/snapshots/latest${snapshotType ? `?snapshot_type=${snapshotType}` : ''}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getSessionHistory(projectId: string): Promise<any[]> {
    const response = await apiClient.get(`/api/v1/sessions/${projectId}/history`);
    return Array.isArray(response.data) ? response.data : [];
  }

  async cleanupSessions(): Promise<any> {
    const response = await apiClient.post('/api/v1/sessions/cleanup');
    return response.data;
  }
}

export const sessionApi = new SessionAPI();
